/**
 * GA4 measurement — the site's single analytics path (BTQ-65 / BTQ-66, 2026-08-04).
 *
 * WHAT THIS REPLACES
 * Measurement used to run through three parallel bootstraps: an inline
 * gtag('config') injected by the Cloudflare Worker, the GTM-T59L9937 container
 * loaded from lib/gtm.ts after consent, and a Google tag whose extra
 * destinations nobody could see in the repo. Every event was therefore delivered
 * to four destinations at once — measured on the live site 04.08.2026:
 *
 *   G-4CSL7CL98Y  inline gtag('config')            standalone Google tag
 *   G-JMXHWVDB3X  GA4 tag inside the GTM container  \
 *   G-FTKVL2FRVX  additional destination             > one Google tag, three
 *   AW-11336189883 Google Ads tag                   /  destinations, set in the UI
 *
 * WHICH PROPERTY IS CANONICAL (corrected 12.08.2026)
 * BTQ-65 first picked G-4CSL7CL98Y, because that is what the Worker's inline
 * snippet configured. That was the wrong end to read it from: G-4CSL7CL98Y is
 * the ORPHANED id. History, verified in GA4 and in the 03/2026 correspondence —
 * on 09.03 the property was linked to Ads 739-672-1062, and on 10.03 the
 * measurement id inside the GTM container was replaced with G-JMXHWVDB3X. The
 * container followed; the Worker's inline config did not, and the repo later
 * inherited it.
 *
 * Today (GA4 admin, account eventboutique2023 / 286383757) the property that
 * carries every EB conversion — "eventboutique2023" / 408079337 — has exactly
 * ONE stream: G-JMXHWVDB3X. G-4CSL7CL98Y belongs to no property reachable from
 * this account, so hits addressed to it are accepted with 204 and discarded.
 * Every Ads conversion action ("eventboutique2023 (web) …") imports from
 * G-JMXHWVDB3X; the contact_click action created there on 10.08 stayed empty for
 * exactly this reason. Hence the id below.
 *
 * DEPLOY COUPLING: the GTM container's Google tag also configures
 * G-JMXHWVDB3X, so once this module targets it too, page_view arrives twice
 * until that tag is paused in GTM. Pause the container's Google tag FIRST, then
 * ship this; its five click tags (mail/phone/whatsapp/telegram_click/send_form)
 * stay until the new contact_click conversion is confirmed to be filling, or EB
 * loses its only live conversion source mid-switch.
 *
 * The Google Ads tag is kept deliberately. It reached the page only through the
 * GTM container's Google tag; dropping the container without re-declaring it here
 * would have silently ended Ads remarketing and conversion tagging.
 *
 * PAGE TITLES
 * Every field identifying the page is passed explicitly from route data.
 * document.title cannot be trusted: on client-side navigation this app never
 * rewrites the head (measured — title, canonical and meta description stay frozen
 * on the landing route), so a title read from the DOM after a route change is the
 * previous page's.
 *
 * CONSENT — Consent Mode v2 (2026-08-06, replaces the hard gate of 04.08)
 * gtag.js loads on every visit, but a consent DEFAULT of "everything denied"
 * (analytics_storage / ad_storage / ad_user_data / ad_personalization,
 * wait_for_update 500ms) is pushed before any config or event. Pre-consent hits
 * therefore go out cookieless (gcs=G100, no _ga cookie is written). Accepting
 * the banner pushes gtag('consent','update',{...granted}) — from then on (and on
 * every later visit, re-pushed at init) collection is full, including the Ads
 * tag. Decline changes nothing: the denied default simply stays. The Worker
 * injects the same default ahead of its GTM container snippet and sets
 * window.__consent_default_set so only one layer pushes it; on Worker-less
 * origins (Pages previews) this module pushes it itself. Yandex.Metrika keeps
 * its own hard gate (Worker-injected, loads only after Accept) — Webvisor must
 * not run without consent.
 *
 * TRANSITION GUARD
 * The Worker still injects the old inline gtag + GTM snippet until it is
 * redeployed. When that snippet is detected, this module leaves page views to it
 * and only adds the lead events, so no deploy order can double-count page views.
 */

import { captureRefSnapshot, enrichContactHref, getRefCode, stripRefTag, trafficType } from "./attribution";

/**
 * The one place the property is named — the live stream of the property Ads
 * imports conversions from (see WHICH PROPERTY IS CANONICAL above). Its sibling
 * destination G-FTKVL2FRVX still collects through the container's Google tag;
 * unlinking it is a GA4 UI job (Google tag -> manage destinations), not a change
 * here.
 */
export const GA_MEASUREMENT_ID = "G-JMXHWVDB3X";

/** Google Ads tag, preserved from the container that used to carry it. */
export const ADS_TAG_ID = "AW-11336189883";

export type ContactMethod = "whatsapp" | "email" | "phone";
export type LinkLocation =
  | "header"
  | "footer"
  | "contact_page"
  | "sticky_lp"
  | "sticky_global"
  | "inline";

/** Written by CookieConsent.tsx; the Worker's Yandex.Metrika stub reads the same cookie. */
const CONSENT_COOKIE = "cookie_consent";
const CONSENT_EVENT = "eb:consent";
const CONTACT_PAGE_PATH = "/contact";

/** How long a page view waits for a lazy route to register its own title. */
const TITLE_WAIT_MS = 1500;

type GtagWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  /** Set by whichever layer (Worker snippet or this module) pushes the consent default first. */
  __consent_default_set?: boolean;
};

interface PageView {
  page_location: string;
  page_path: string;
  page_title?: string;
}

const win = (): GtagWindow => window as unknown as GtagWindow;

let initialised = false;
let legacyWorkerBootstrap = false;
let queuePrimed = false;
let gtagRequested = false;
let lastPageViewKey: string | null = null;
let pendingTitleView: { view: PageView; timer: number } | null = null;

const registeredTitles = new Map<string, string>();

/* ------------------------------------------------------------------ consent */

export function getCookieConsent(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)cookie_consent=([^;]*)/);
  return match ? match[1] : null;
}

export function setCookieConsent(value: "accepted" | "declined"): void {
  const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${CONSENT_COOKIE}=${value};expires=${expires};path=/;SameSite=Lax`;
}

export function analyticsConsentGranted(): boolean {
  try {
    return getCookieConsent() === "accepted";
  } catch {
    return false;
  }
}

/* -------------------------------------------------------------------- gtag */

function ensureGtag(): (...args: unknown[]) => void {
  const w = win();
  w.dataLayer = w.dataLayer || [];
  if (!w.gtag) {
    // Canonical stub. gtag.js replays the queue expecting `arguments` objects,
    // so this pushes `arguments` — a plain array is not equivalent.
    w.gtag = function gtagStub() {
      (win().dataLayer as unknown[]).push(arguments);
    };
  }
  return w.gtag;
}

function gtag(...args: unknown[]): void {
  ensureGtag()(...args);
}

/** True when the Worker's pre-BTQ-65 inline snippet already configured a property. */
function workerSnippetPresent(): boolean {
  const queue = win().dataLayer;
  if (!Array.isArray(queue)) return false;
  return queue.some((entry) => {
    if (!entry || typeof entry !== "object") return false;
    const args = entry as ArrayLike<unknown>;
    return (
      args.length >= 2 &&
      args[0] === "config" &&
      typeof args[1] === "string" &&
      (args[1] as string).startsWith("G-")
    );
  });
}

/* ----------------------------------------------------- Consent Mode v2 */

/**
 * The denied-everything default MUST precede any config/event in the queue.
 * On Worker-served pages the head snippet has already pushed it (and set the
 * flag); this covers Pages previews and any other Worker-less origin.
 */
function pushConsentDefault(): void {
  const w = win();
  if (w.__consent_default_set) return;
  w.__consent_default_set = true;
  gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });
}

function pushConsentUpdate(state: "granted" | "denied"): void {
  gtag("consent", "update", {
    analytics_storage: state,
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  });
}

/**
 * Queue bootstrap and script fetch are split on purpose: the queue (consent
 * default → js → config) is primed synchronously at init so any event pushed
 * later lands AFTER config and replays correctly, while the ~90 KB gtag.js
 * fetch stays deferred to idle/first interaction and never competes with the
 * LCP path. Merging the two — fetching the script whenever an event fires —
 * would start the download at React-commit time on every load.
 */
function primeGtagQueue(): void {
  if (queuePrimed || legacyWorkerBootstrap) return;
  queuePrimed = true;
  pushConsentDefault();
  gtag("js", new Date());
  // send_page_view:false — page views are sent by trackRouteChange with explicit
  // route data. Leaving it on would add a second, DOM-derived hit per navigation.
  gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
  gtag("config", ADS_TAG_ID);
}

function requestGtagScript(): void {
  if (gtagRequested || legacyWorkerBootstrap) return;
  gtagRequested = true;
  primeGtagQueue();
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

/**
 * Keeps the deferred-load behaviour the Worker snippet had: the tag is fetched on
 * idle or at the first sign of interaction, whichever comes first, so it never
 * competes with the LCP path. Events queued before it lands are replayed by gtag.js.
 */
function scheduleGtagScript(): void {
  if (gtagRequested || legacyWorkerBootstrap) return;
  const start = () => requestGtagScript();
  const w = win();
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(start, { timeout: 4000 });
  } else {
    window.setTimeout(start, 3000);
  }
  (["mousedown", "touchstart", "keydown", "scroll", "wheel", "submit"] as const).forEach((type) => {
    window.addEventListener(type, start, { once: true, passive: true, capture: true });
  });
}

/**
 * Business events mirrored into dataLayer as GTM custom events (BTQ-97), so
 * container-side pixels — Meta today, whatever marketing adds tomorrow — can
 * trigger on a lead without a code change and a deploy.
 *
 * GA4 is fed by gtag above and never by these pushes: the GA4 tags inside both
 * containers are paused. Wiring a GA4 tag onto these events would deliver every
 * one of them twice — exactly the double-count killed on 13.08.
 *
 * page_view is deliberately not mirrored: it fires on every SPA navigation and
 * no third-party tag consumes it as a custom event.
 */
const DATALAYER_MIRRORED = new Set(["contact_click", "generate_lead", "form_visible", "scroll_depth"]);

function mirrorToDataLayer(eventName: string, params: Record<string, unknown>): void {
  if (!DATALAYER_MIRRORED.has(eventName)) return;
  try {
    const w = win();
    w.dataLayer = w.dataLayer || [];
    (w.dataLayer as unknown[]).push({ event: eventName, ...params });
  } catch {
    /* dataLayer unavailable — mirroring is best-effort, GA4 delivery is unaffected */
  }
}

function send(eventName: string, params: Record<string, unknown>): void {
  // Consent Mode v2: no consent check — pre-consent events go out cookieless
  // under the denied default. EXCEPT under the legacy Worker snippet, which
  // predates Consent Mode: it configures GA4 ungated at head-parse time, and a
  // default pushed after config cannot protect the hit, so there the old hard
  // gate stays until the Worker is redeployed.
  if (legacyWorkerBootstrap && !analyticsConsentGranted()) return;
  primeGtagQueue();
  gtag("event", eventName, params);
  mirrorToDataLayer(eventName, params);
}

/* --------------------------------------------------------------- page views */

function normalisePath(pathname: string): string {
  const withoutQuery = pathname.replace(/[?#].*$/, "");
  return withoutQuery.length > 1 ? withoutQuery.replace(/\/+$/, "") : withoutQuery;
}

function resolveTitle(pathname: string): string | undefined {
  return registeredTitles.get(normalisePath(pathname));
}

function clearPendingTitleView(): void {
  if (!pendingTitleView) return;
  window.clearTimeout(pendingTitleView.timer);
  pendingTitleView = null;
}

function emitPageView(view: PageView): void {
  const key = `${view.page_path}|${view.page_title ?? ""}`;
  if (key === lastPageViewKey) return;
  lastPageViewKey = key;
  send("page_view", { ...view });
}

/**
 * Called once per router location change. Every page here renders <SEOHead>, but
 * routes are lazy, so on a first visit the page has not mounted yet: the view
 * waits for registerRouteTitle() and goes out without a title only if the route
 * never provides one (an empty field beats the previous page's title).
 */
export function trackRouteChange(pathWithSearch: string): void {
  if (legacyWorkerBootstrap) return;
  const view: PageView = {
    page_location: window.location.href,
    page_path: pathWithSearch,
  };
  const title = resolveTitle(window.location.pathname);
  clearPendingTitleView();

  if (title) {
    emitPageView({ ...view, page_title: title });
    return;
  }
  pendingTitleView = {
    view,
    timer: window.setTimeout(() => {
      pendingTitleView = null;
      emitPageView(view);
    }, TITLE_WAIT_MS),
  };
}

/** Called by <SEOHead> so the page's own title is the one GA4 receives. */
export function registerRouteTitle(title: string, pathname?: string): void {
  if (typeof window === "undefined" || !title) return;
  const key = normalisePath(pathname ?? window.location.pathname);
  registeredTitles.set(key, title);

  const pending = pendingTitleView;
  if (!pending) return;
  if (normalisePath(pending.view.page_path) !== key) return;
  window.clearTimeout(pending.timer);
  pendingTitleView = null;
  emitPageView({ ...pending.view, page_title: title });
}

/* -------------------------------------------------------------- lead events */

export function classifyContactHref(href: string): ContactMethod | null {
  const value = href.trim();
  if (!value) return null;
  if (/^tel:/i.test(value)) return "phone";
  if (/^mailto:/i.test(value)) return "email";
  if (/^whatsapp:/i.test(value)) return "whatsapp";
  try {
    const url = new URL(value, window.location.href);
    if (/(^|\.)(wa\.me|whatsapp\.com)$/i.test(url.hostname)) return "whatsapp";
  } catch {
    return null;
  }
  return null;
}

const LINK_LOCATIONS: readonly LinkLocation[] = [
  "header",
  "footer",
  "contact_page",
  "sticky_lp",
  "sticky_global",
  "inline",
];

function resolveLinkLocation(anchor: Element): LinkLocation {
  const declared = anchor.closest("[data-analytics-location]")?.getAttribute("data-analytics-location");
  if (declared && (LINK_LOCATIONS as readonly string[]).includes(declared)) {
    return declared as LinkLocation;
  }
  if (anchor.closest("header")) return "header";
  if (anchor.closest("footer")) return "footer";
  if (normalisePath(window.location.pathname) === CONTACT_PAGE_PATH) return "contact_page";
  return "inline";
}

export function trackContactClick(params: {
  method: ContactMethod;
  link_location: LinkLocation;
  link_url: string;
}): void {
  // traffic_bucket/lead_ref ride on every lead event (spec 06.08 §6). The param
  // is NOT called traffic_type: that name is reserved by GA4 for the internal
  // traffic filter — measured on the live site 12.08.2026, gtag swallowed it and
  // sent tt=direct instead of ep.traffic_type, so it never reaches a report and
  // cannot be registered as a custom dimension. link_url is the href AS AUTHORED
  // — the click-time ref enrichment must not leak into the GA4 dimension or
  // every visitor becomes a unique link_url value.
  send("contact_click", { ...params, traffic_bucket: trafficType(), lead_ref: getRefCode() });
}

/**
 * Success of a form submission — GA4's recommended lead event. Call it on the
 * branch that ran after the server accepted the submission, never on the button
 * click: a click counts failed attempts and abandoned Turnstile challenges too.
 */
export function trackGenerateLead(params: { form_id: string; page_path?: string }): void {
  send("generate_lead", {
    form_id: params.form_id,
    page_path: params.page_path ?? `${window.location.pathname}${window.location.search}`,
    traffic_bucket: trafficType(),
    lead_ref: getRefCode(),
  });
}

/**
 * A submission the visitor made and we failed to accept (BTQ-99).
 *
 * Counterpart to generate_lead: together they give the pair "attempted" vs
 * "accepted", so a broken form shows up as a gap in GA4 instead of waiting to
 * be noticed by hand. That gap is what BTQ-98 cost — every enquiry without a
 * phone number returned 500 for an unknown number of days, and nothing anywhere
 * counted it.
 *
 * error_code carries the reason the endpoint gave (duplicate_email,
 * captcha_failed, http_500 ...) so a spike can be told apart from routine
 * duplicate submissions without opening logs.
 */
export function trackFormError(params: { form_id: string; error_code: string; page_path?: string }): void {
  send("form_error", {
    form_id: params.form_id,
    error_code: String(params.error_code || "unknown").slice(0, 60),
    page_path: params.page_path ?? `${window.location.pathname}${window.location.search}`,
    traffic_bucket: trafficType(),
  });
}

/**
 * Generic sender for sibling measurement modules (engagement.ts). Routed through
 * the same send() as everything else here, so the consent gate, the queue prime
 * and the legacy-Worker guard apply identically — a second gtag path would drift.
 */
export function trackEvent(eventName: string, params: Record<string, unknown> = {}): void {
  send(eventName, params);
}

/**
 * One delegated listener for every contact link on the site. Bound to document in
 * the capture phase rather than to the links themselves: this is a SPA, anchors
 * mount and unmount constantly, and per-element binding silently stops covering
 * links the next time the markup is refactored.
 */
function handleDocumentClick(event: MouseEvent): void {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const anchor = target.closest("a[href]");
  if (!anchor) return;
  const href = anchor.getAttribute("href") ?? "";
  const method = classifyContactHref(href);
  if (!method) return;
  // Stitch the visitor's ref code into the outgoing WhatsApp text / email
  // subject before the browser follows the link. Capture phase runs before
  // navigation starts, so mutating href here is safe; links that already carry
  // a [ref:] (e.g. StickyWhatsApp builds its own) pass through unchanged.
  if (method === "whatsapp" || method === "email") {
    const enriched = enrichContactHref(href, method);
    if (enriched !== href) anchor.setAttribute("href", enriched);
  }
  // link_url must aggregate in GA4: strip the per-visitor ref tag, which is
  // already present on repeat clicks (the mutation above) and on hrefs born
  // enriched (StickyWhatsApp).
  // A WhatsApp click is the one contact route whose origin is otherwise lost:
  // the enquiry arrives in the messenger carrying only the ref code. Snapshot
  // what that code means before the page goes away (BTQ-88).
  if (method === "whatsapp") captureRefSnapshot();

  trackContactClick({ method, link_location: resolveLinkLocation(anchor), link_url: stripRefTag(href) });
}

/* --------------------------------------------------------------------- init */

function onConsentChange(): void {
  if (!analyticsConsentGranted()) {
    // Decline: the default is already denied, but an explicit update makes the
    // state unambiguous (acceptance note in BTQ-65, 07.08) and holds even if a
    // future UI lets a visitor revoke an earlier Accept.
    pushConsentUpdate("denied");
    return;
  }
  // Accept: flip Consent Mode to granted and stop waiting for idle — the tag
  // should be live before the visitor's next click.
  pushConsentUpdate("granted");
  requestGtagScript();
}

export function initAnalytics(): void {
  if (initialised || typeof window === "undefined") return;
  initialised = true;
  // Must be read before this module pushes anything of its own.
  legacyWorkerBootstrap = workerSnippetPresent();
  primeGtagQueue();
  // Returning visitor who accepted earlier: restore granted state before any hit.
  if (analyticsConsentGranted()) pushConsentUpdate("granted");
  document.addEventListener("click", handleDocumentClick, true);
  window.addEventListener(CONSENT_EVENT, onConsentChange);
  // Consent Mode v2: the tag always loads (deferred to idle/first interaction);
  // the consent state only decides whether hits carry cookies.
  scheduleGtagScript();
}
